import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/auth.context";
import { FillerStatus, IFiller } from "../../types/filler.type";
import { canEditFillers } from "../../utils/roleUtils";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import useGetRequest from "../../hooks/useGetRequest";
import { UserRole } from "../../types/user.type";
import { Machine } from "../../types/machine.type";
import { DownOutlined } from "@ant-design/icons";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function Fillers() {
  const { authState } = useAuth();
  const [fillers, setFillers] = useState<IFiller[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [status, setStatus] = useState<FillerStatus | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedFiller, setSelectedFiller] = useState<IFiller | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { data: machineList, loading: machineListLoading } =
    useGetRequest("/machine/list");

  useEffect(() => {
    fetchFillers();
  }, [page, searchTerm, pageSize, status, sortBy, sortOrder]);

  const fetchFillers = async () => {
    setLoading(true);

    try {
      const response = await api.get("/filler", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          status,
          sortBy,
          order: sortOrder,
        },
      });

      setFillers(response.data.fillers);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching fillers", error);
      message.error("Ошибка при получение филлеров.");
    } finally {
      setLoading(false);
      form.resetFields();
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleOpenModal = (filler?: IFiller) => {
    if (filler) {
      setSelectedFiller(filler);
      form.setFieldsValue(filler);
    }

    setIsModalOpen(true);
  };

  const handleSubmitForm = async (values: Partial<IFiller>) => {
    setConfirmLoading(true);

    try {
      if (selectedFiller) {
        await api.put(`/filler/${selectedFiller._id}`, values);
        message.success("Филлер успешно обновлен.");
      } else {
        await api.post("/filler", values);
        message.success("Филлер успешно добавлен.");
      }

      fetchFillers();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving filler", error);
      message.error(error.response.data.error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/filler/${id}`);
      message.success("Филлер успешно удален.");
      fetchFillers();
    } catch (error: any) {
      console.error("Error deleting filler", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Кол-во",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Обновлен",
      dataIndex: "lastUpdate",
      key: "lastUpdate",
    },
    {
      title: "Добавил",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, filler: IFiller) => {
        const menu = (
          <Menu>
                            {canEditFillers(authState.user?.role) && (
                  <>
                    <Menu.Item onClick={() => handleOpenModal(filler)}>
                      Изменить
                    </Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этот филлер?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDelete(filler._id)}
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Филлеры</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />

        <Select
          placeholder="Фильтр по статусу"
          onChange={(value) => setStatus(value)}
          allowClear
          style={{ width: 200 }}
        >
          {Object.values(FillerStatus).map((unit) => (
            <Option key={unit} value={unit}>
              {unit}
            </Option>
          ))}
        </Select>

        <Select
          placeholder="Сортировать по"
          defaultValue="name"
          onChange={(value) => setSortBy(value)}
          style={{ width: 150 }}
        >
          <Option value="name">Название</Option>
          <Option value="quantity">Кол-во</Option>
          <Option value="createdAt">Дата</Option>
        </Select>
        <Select
          defaultValue="asc"
          onChange={(value: "asc" | "desc") => setSortOrder(value)}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        {canEditFillers(authState.user?.role) && (
          <Button type="primary" onClick={() => handleOpenModal()}>
            Добавить
          </Button>
        )}
      </Space>

      <Table
        loading={loading}
        columns={columns}
        dataSource={fillers}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total,
          pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={selectedFiller ? "Изменить" : "Добавить"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите Название" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="quantity"
            label="Кол-во"
            rules={[{ required: true, message: "Введите кол-во" }]}
          >
            <InputNumber />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите Описание" }]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item
            name="machineId"
            label="Станок"
            rules={[{ required: true, message: "Выберите станок" }]}
          >
            <Select
              placeholder="Выберите станок"
              allowClear
              loading={machineListLoading}
            >
              {machineList?.map((machine: Partial<Machine>) => (
                <Option key={machine._id} value={machine._id}>
                  {machine.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: "Выберите статус" }]}
          >
            <Select placeholder="Выберите статус" allowClear>
              {Object.values(FillerStatus).map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
